import { useCallback, useEffect, useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { Navigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";

import { TopInfo } from "../../components/top-info/top-info";
import { userAtom } from "../../../services/atoms/auth.atoms";
import { ROUTES } from "../../../helpers/constants";
import { formatCLP } from "../../../helpers/functions";
import TableComponent from "../../../components/table/table-component";
import GuestPaymentsAPI from "../../../services/api/guest-payments.api";
import CatalogAPI from "../../../services/api/catalog.api";
import AssignModal from "./components/assign-modal";

const topInfo = { title: "Pagos por asignar", subtitle: "Pagos pendientes de vinculación a un cliente" };

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" }) : "";

const formatAmount = (amount, currency) =>
  currency === "CLP" ? formatCLP(amount) : `${amount ?? 0} ${currency || ""}`.trim();

const GuestPayments = () => {
  const user = useRecoilValue(userAtom);
  const isSuperAdmin = !!user?.data?.isSuperAdmin;

  const [payments, setPayments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPayments = useCallback(() => {
    setLoading(true);
    GuestPaymentsAPI.list()
      .then((res) => {
        setPayments(res?.data ?? []);
      })
      .catch(() => {
        toast.error("No pudimos cargar los pagos pendientes");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPayments();
    }
  }, [isSuperAdmin, fetchPayments]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    CatalogAPI.getCatalog()
      .then((res) => setCatalog(res?.data ?? []))
      .catch(() => {});
  }, [isSuperAdmin]);

  const planName = (catalogId) => catalog.find((entry) => entry.id === catalogId)?.name ?? catalogId;

  const handleOpenModal = (payment) => {
    setSelectedPayment(payment);
    setModalOpen(true);
  };

  const handleToggleModal = () => {
    setModalOpen((open) => !open);
    if (modalOpen) setSelectedPayment(null);
  };

  const handleAssigned = () => {
    setModalOpen(false);
    setSelectedPayment(null);
    fetchPayments();
  };

  const columns = useMemo(() => [
    { Header: "Empresa", accessor: ({ payer }) => <div>{payer?.companyName}</div> },
    { Header: "RUT", accessor: ({ payer }) => <div>{payer?.rut}</div> },
    { Header: "Email", accessor: ({ payer }) => <div>{payer?.email}</div> },
    { Header: "Teléfono", accessor: ({ payer }) => <div>{payer?.phone}</div> },
    { Header: "Plan", accessor: (row) => <div>{planName(row.catalogId)}</div> },
    { Header: "Monto", accessor: (row) => <div>{formatAmount(row.amount, row.currency)}</div> },
    { Header: "Fecha", accessor: (row) => <div>{formatDate(row.createdAt)}</div> },
    {
      Header: " ",
      accessor: (row) => (
        <button type="button" className="gm-primary-button" onClick={() => handleOpenModal(row)}>
          Vincular
        </button>
      ),
      disableSortBy: true,
      disableFilters: true,
    },
  ], [catalog, handleOpenModal]);

  if (!isSuperAdmin) {
    return <Navigate to={ROUTES.CLIENTS.route} replace />;
  }

  return (
    <div className="gm-clients-screen-container">
      <Toaster />
      <TopInfo info={topInfo} />

      <AssignModal
        open={modalOpen}
        toggle={handleToggleModal}
        payment={selectedPayment}
        onAssigned={handleAssigned}
      />

      <TableComponent
        data={payments}
        loading={loading}
        columns={columns}
        section={{ plural: "pagos", singular: "pago" }}
        initialPageSize={Math.max(payments.length, 1)}
        pagination={false}
      />
    </div>
  );
};

export default GuestPayments;
